import React from "react";

import InvestorSectionHeading from "./InvestorSectionHeading";
import { InvestorSectionIcon } from "./InvestorMetricIcons";
import {
  formatExpectedReturnPctWithPa,
  formatProjectCodeDisplay,
  getMarketStageToneClass,
  getProjectLifecycleLabel,
} from "../../utils/projectViews";
import { formatProjectSupplySummary } from "../../utils/projectShareMetrics";
import { REDEEMED_PROJECTS_PARENT_LABEL } from "../../utils/investorNavConfig";

function formatPerformanceLabel(status) {
  const raw = String(status || "").trim();
  if (!raw) return "Performance N/A";
  return raw.replaceAll("_", " ");
}

function OpportunityProjectCard({ project, showMeta = false }) {
  const code = formatProjectCodeDisplay(project?.project_code);
  const stageClass = getMarketStageToneClass(project?.status);
  const stageLabel = getProjectLifecycleLabel(project?.status);
  const returnPct = formatExpectedReturnPctWithPa(project);

  return (
    <Link to={`/investments?project_id=${project.id}`} className="invx-opportunity-card">
      <div className="invx-opportunity-card__body">
        <div className="invx-opportunity-card__primary">
          <span className="invx-opportunity-card__code">{code}</span>
          <span className="invx-opportunity-card__name">{project.name}</span>
          <span className={`invx-opportunity-pill invx-opportunity-pill--${stageClass.replace("invx-market-stage--", "")}`}>
            {stageLabel}
          </span>
        </div>
        {showMeta ? (
          <div className="invx-opportunity-card__meta">
            {returnPct ? (
              <span className="invx-opportunity-meta-chip">
                Est. ret. <strong>{returnPct}</strong>
              </span>
            ) : null}
            <span className="invx-opportunity-meta-chip">{formatPerformanceLabel(project.performance_status)}</span>
            <span className="invx-opportunity-meta-chip">{formatProjectSupplySummary(project)}</span>
          </div>
        ) : null}
      </div>
      <span className="invx-opportunity-card__chevron" aria-hidden>
        →
      </span>
    </Link>
  );
}

function OpportunityGroup({ icon, title, description, count, children, isEmpty }) {
  return (
    <div className="invx-opportunity-group">
      <div className="invx-opportunity-group__head">
        <InvestorSectionHeading icon={icon} className="invx-opportunity-group__title">
          {title}
        </InvestorSectionHeading>
        {count != null ? (
          <span className="invx-opportunity-group__count">
            {count} project{count === 1 ? "" : "s"}
          </span>
        ) : null}
      </div>
      {description ? <p className="invx-opportunity-group__desc">{description}</p> : null}
      {isEmpty ? (
        <div className="invx-opportunity-empty">
          <span className="invx-opportunity-empty__icon" aria-hidden>
            <InvestorSectionIcon name={icon} />
          </span>
          <p>None yet.</p>
        </div>
      ) : (
        <div className="invx-opportunity-group__list">{children}</div>
      )}
    </div>
  );
}

export default function InvestmentOpportunitiesSection({
  activeProjects = [],
  redeemedProjects = [],
}) {
  const showActive = activeProjects.length > 0;
  const showRedeemed = redeemedProjects.length > 0;
  if (!showActive && !showRedeemed) return null;

  return (
    <section className="investor-section investor-opportunities" aria-labelledby="investment-opportunities-heading">
      <header className="investor-opportunities__header">
        <InvestorSectionHeading icon="opportunities" id="investment-opportunities-heading">
          Investment Opportunities
        </InvestorSectionHeading>
        <p className="investor-opportunities__lead">
          {showRedeemed && !showActive
            ? "Redeemed listings you participated in."
            : showRedeemed
              ? "Fundraising and funded market projects, plus redeemed listings you participated in."
              : "Fundraising and funded market projects."}
        </p>
      </header>

      {showActive ? (
        <OpportunityGroup
          icon="opportunities"
          title="Fundraising & funded market"
          count={activeProjects.length}
          isEmpty={false}
        >
          {activeProjects.map((p) => (
            <OpportunityProjectCard key={p.id} project={p} />
          ))}
        </OpportunityGroup>
      ) : null}

      {showRedeemed ? (
        <OpportunityGroup
          icon="redeemed-projects"
          title={REDEEMED_PROJECTS_PARENT_LABEL}
          description="Projects you redeemed that are now fully settled to SSA / parent company ownership."
          count={redeemedProjects.length}
          isEmpty={false}
        >
          {redeemedProjects.map((p) => (
            <OpportunityProjectCard key={`redeemed-${p.id}`} project={p} showMeta />
          ))}
        </OpportunityGroup>
      ) : null}
    </section>
  );
}
